import type {
	JiraTopBanner,
	JiraTopBannerMessageMetadata,
} from '@post-office/placement-create-pipeline/pipes';
import {
	createClientPipeline,
	createServerPipeline,
} from '@post-office/placement-create-pipeline/create-pipeline';
import { type GenerateValidationFor } from '@atlassian/vindicated';

import { type MessageTemplateContext } from '../../..';
import { type AnyHydrationResult } from '@post-office/shared-contracts/placement-pipe/pipeline-types';
import { type RequestContext } from '@post-office/shared-contracts/shared-request-middleware-context';
import { type JSX } from 'react/jsx-runtime';
import type { TransformMetadataResult } from '@post-office/shared-contracts/message-template-metadata';

export type HydrationResult = { readMoreLink: string };

export type VHydrationResult = GenerateValidationFor<HydrationResult>;

type Pipe = JiraTopBanner<{
	messageContext: MessageTemplateContext;
	hydrationResult: HydrationResult;
}>;

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - Pipe type variance issue with AnyPlacementPipe constraint (JiraTopBanner uses Record<string, never> for PlacementContext)
const clientPipeline = createClientPipeline<Pipe>();
export const createRender: <
	U extends (renderProps: Omit<HydrationResult, 'postOffice'>) => JSX.Element | null,
>(
	render: U,
) => (renderProps: Omit<HydrationResult, 'postOffice'>) => JSX.Element | null =
	clientPipeline.createRender;
export const createValidate: (
	validate: (apiResponse: unknown) => HydrationResult,
) => (apiResponse: unknown) => HydrationResult = clientPipeline.createValidate;
// prettier-ignore
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - Pipe type variance issue with AnyPlacementPipe constraint (JiraTopBanner uses Record<string, never> for PlacementContext)
const serverPipeline = createServerPipeline<Pipe>();
export const createHydrate: (
	hydrate: (
		requestContext: RequestContext,
		placementContext: {
			[x: string]: never;
		},
		messageContext: {
			readMoreLink: string;
		} & {
			messageInstanceId: string;
			eventTime: string;
		},
	) => Promise<HydrationResult>,
) => (
	requestContext: RequestContext,
	placementContext: {
		[x: string]: never;
	},
	messageContext: {
		readMoreLink: string;
	} & {
		messageInstanceId: string;
		eventTime: string;
	},
) => Promise<AnyHydrationResult> = serverPipeline.createHydrate;
export const createHydrateExample: (
	hydrateExample: (
		requestContext: Partial<RequestContext>,
		placementContext: {
			[x: string]: never;
		},
		messageContext: {
			readMoreLink: string;
		},
	) => HydrationResult,
	config?:
		| {
				requestContext?: Partial<RequestContext>;
		  }
		| undefined,
) => (
	requestContext: Partial<RequestContext>,
	placementContext: {
		[x: string]: never;
	},
	messageContext: {
		readMoreLink: string;
	} & {
		messageInstanceId: string;
		eventTime: string;
	},
) => Omit<HydrationResult, 'postOffice'> = serverPipeline.createHydrateExample;
export const createTransformMetadata: (
	transformMetadata: (params: {
		messageContext: {
			readMoreLink: string;
		};
	}) => JiraTopBannerMessageMetadata,
) => (params: {
	messageContext: {
		readMoreLink: string;
	};
}) => TransformMetadataResult = serverPipeline.createTransformMetadata;
